"use client";

import Script from "next/script";
import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useRef } from "react";
import { GA4_MEASUREMENT_ID } from "@/lib/seo/site";

/**
 * GA4 (2026-10-13) — 측정 ID 는 src/lib/seo/site.ts::GA4_MEASUREMENT_ID (대표 전달 · 공개 값) · env `NEXT_PUBLIC_GA4_ID` 가 있으면 env 우선 · gtag.js 는 afterInteractive.
 * 첫 화면은 gtag config 가 page_view 를 보내고, 이후 클라이언트 이동마다 page_view 를 다시 보낸다.
 */
const GA_ID = process.env.NEXT_PUBLIC_GA4_ID?.trim() || GA4_MEASUREMENT_ID;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function RouteTracker() {
  const pathname = usePathname();
  const sp = useSearchParams();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false; // 첫 화면은 gtag config 가 보냄
      return;
    }
    if (!window.gtag) return;
    const q = sp?.toString();
    window.gtag("event", "page_view", { page_path: q ? `${pathname}?${q}` : pathname, page_location: window.location.href });
  }, [pathname, sp]);
  return null;
}

export default function GoogleAnalytics() {
  if (!/^G-[A-Z0-9]+$/.test(GA_ID)) return null;
  return (
    <>
      <Script src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`} strategy="afterInteractive" />
      <Script id="ga4-init" strategy="afterInteractive">
        {`window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${GA_ID}');`}
      </Script>
      <Suspense fallback={null}>
        <RouteTracker />
      </Suspense>
    </>
  );
}
